/**
 * Per-conversation mute settings.
 * Port of flock-expo/src/hooks/useChatMute.ts (Alert → silent revert + log).
 */
import { useCallback, useState } from 'react';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/context/AuthContext';

interface MuteStates {
  [key: string]: boolean; // key = chatGroupId or 'everyone'
}

export function useChatMute() {
  const { isSwitchingOrg } = useAuth();
  const [muteStates, setMuteStates] = useState<MuteStates>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchMuteStates = useCallback(
    async (groupId: string) => {
      if (isSwitchingOrg) return;
      try {
        const response = await api.get<{
          data: {
            notificationLevel: string;
            muteUntil: string | null;
            mutedChats?: Record<string, boolean>;
          };
        }>(`/groups/${groupId}/chat/settings`);
        if (response.data) {
          const mutedChats = response.data.mutedChats || {};
          const newStates: MuteStates = {};
          for (const [key, value] of Object.entries(mutedChats)) {
            newStates[key === groupId ? 'everyone' : key] = value;
          }
          setMuteStates(newStates);
        }
      } catch (error) {
        console.error('[useChatMute] Error fetching mute states:', error);
      }
    },
    [isSwitchingOrg]
  );

  const toggleMute = useCallback(
    async (groupId: string, chatGroupId?: string) => {
      const key = chatGroupId || 'everyone';
      const currentlyMuted = muteStates[key] || false;
      const newLevel = currentlyMuted ? 'all' : 'none';

      setMuteStates((prev) => ({ ...prev, [key]: !currentlyMuted }));
      setIsLoading(true);
      try {
        await api.put(`/groups/${groupId}/chat/settings`, {
          notificationLevel: newLevel,
          ...(chatGroupId ? { chatGroupId } : {}),
        });
      } catch (error) {
        setMuteStates((prev) => ({ ...prev, [key]: currentlyMuted }));
        console.error('[useChatMute] Error toggling mute:', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [muteStates]
  );

  return { muteStates, toggleMute, isLoading, fetchMuteStates };
}

export default useChatMute;
